import React, { useEffect, useRef, useState, useCallback, useMemo, memo } from 'react';
import './DesktopFeed.css';
import WordReveal from '../shared/WordReveal';

// Separate input component to prevent full feed re-renders on typing
const FeedInput = memo(function FeedInput({ onSubmit, isSubmitting, placeholder, isCompact, onFocusChange, onAddPerson, isOnboarding }) {
  const [input, setInput] = useState('');
  const inputRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || isSubmitting) return;
    onSubmit(input.trim());
    setInput('');
    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
  };

  return (
    <div className={`feed-input-area ${isCompact ? 'compact' : ''}`}>
      <form className={`feed-input-container ${isOnboarding ? 'onboarding' : ''}`} onSubmit={handleSubmit}>
        {onAddPerson && (
          <button
            type="button"
            className="quick-add-person-btn"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              onAddPerson({ top: rect.top - 450, left: rect.left });
            }}
            title="Quick add person"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="8.5" cy="7" r="4"/>
              <line x1="20" y1="8" x2="20" y2="14"/>
              <line x1="23" y1="11" x2="17" y2="11"/>
            </svg>
          </button>
        )}
        <textarea
          ref={inputRef}
          rows={1}
          className={`feed-input ${isOnboarding ? 'onboarding-input' : ''}`}
          placeholder={placeholder || 'What happened? Ask anything...'}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            // Auto-resize textarea
            e.target.style.height = 'auto';
            e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && input.trim()) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
          onFocus={() => onFocusChange(true)}
          onBlur={() => onFocusChange(false)}
          disabled={isSubmitting}
        />
        <button type="submit" className="feed-send-btn" disabled={isSubmitting || !input.trim()}>
          <span>↑</span>
        </button>
      </form>
    </div>
  );
});

// Smooth text reveal animation
// Optional renderFn applies formatting (like name highlighting) to the displayed text
function SmoothTextReveal({ text, renderFn }) {
  const [displayedLength, setDisplayedLength] = useState(0);
  const targetLengthRef = useRef(0);
  const animationRef = useRef(null);

  useEffect(() => {
    targetLengthRef.current = text.length;
    const animate = () => {
      setDisplayedLength(prev => {
        if (prev >= targetLengthRef.current) return prev;
        const charsToAdd = Math.max(1, Math.ceil((targetLengthRef.current - prev) / 20));
        return Math.min(prev + charsToAdd, targetLengthRef.current);
      });
      animationRef.current = requestAnimationFrame(animate);
    };
    animationRef.current = requestAnimationFrame(animate);
    return () => { if (animationRef.current) cancelAnimationFrame(animationRef.current); };
  }, [text]);

  const displayedText = text.slice(0, displayedLength);
  const isComplete = displayedLength >= text.length;
  const content = renderFn ? renderFn(displayedText) : displayedText;

  return (
    <div className="smooth-text-reveal">
      {content}
      {!isComplete && <span className="streaming-cursor" />}
    </div>
  );
}

// Using unified WordReveal from shared components

// Inline prompt for creating first group (onboarding)
function CreateGroupPrompt({ forPerson, suggestedName, onCreateGroup }) {
  const [groupName, setGroupName] = useState(suggestedName || '');
  const [isCreating, setIsCreating] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleCreate = async () => {
    if (!groupName.trim() || isCreating) return;
    setIsCreating(true);
    await onCreateGroup(groupName.trim());
  };

  return (
    <div className="create-group-prompt">
      <div className="create-group-header">
        <div className="create-group-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        </div>
        <div className="create-group-text">
          {forPerson ? (
            <>Create a group to add <strong>{forPerson}</strong> to:</>
          ) : (
            <>Create your first group:</>
          )}
        </div>
      </div>
      <div className="create-group-input-row">
        <input
          ref={inputRef}
          type="text"
          className="create-group-input"
          placeholder="e.g., Investors, Board Members, Donors..."
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleCreate();
          }}
          disabled={isCreating}
        />
        <button
          className="create-group-btn"
          onClick={handleCreate}
          disabled={!groupName.trim() || isCreating}
        >
          {isCreating ? (
            <div className="create-group-spinner"></div>
          ) : (
            <>Create & Add</>
          )}
        </button>
      </div>
    </div>
  );
}

function DesktopFeed({
  entries,
  loading,
  donors,
  groups,
  streamingText,
  streamingEntryId,
  onNameTap,
  onSubmit,
  isSubmitting,
  placeholder,
  scrollToEntryId,
  onScrollComplete,
  // Action handlers - shared logic from DesktopApp
  onGroupSelect,
  onSkipGroupSelect,
  onCreateGroup,
  onViewList,
  onCreateListForGroup,
  onConfirmAdd,
  onDeclineAdd,
  onResearchPerson,
  onSkipResearch,
  onSkipMoreInfo,
  onAddPerson,
  // Onboarding props
  showWelcome,
  onSaveUserContext,
  isOnboarding
}) {
  const feedEndRef = useRef(null);
  const feedContainerRef = useRef(null);
  const entryRefs = useRef({});
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [highlightedEntryId, setHighlightedEntryId] = useState(null);
  const [animatingEntryId, setAnimatingEntryId] = useState(null);
  const prevStreamingEntryId = useRef(null);
  const isInitialLoad = useRef(true);
  const prevEntriesLength = useRef(0);

  // Detect when streaming ends and trigger word animation
  useEffect(() => {
    // When streaming ends (was streaming, now not), animate the entry
    if (prevStreamingEntryId.current && !streamingEntryId) {
      setAnimatingEntryId(prevStreamingEntryId.current);
      // Clear animation after it completes
      const entry = entries.find(e => e.id === prevStreamingEntryId.current);
      if (entry?.message) {
        const wordCount = entry.message.split(/\s+/).filter(w => w.trim()).length;
        setTimeout(() => setAnimatingEntryId(null), wordCount * 50 + 500);
      }
    }
    prevStreamingEntryId.current = streamingEntryId;
  }, [streamingEntryId, entries]);

  // Generate consistent color for a name
  const getAvatarColor = (name) => {
    const colors = ['#a78bfa', '#60a5fa', '#34d399', '#2dd4bf', '#818cf8', '#22d3d1'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Render markdown
  const renderMarkdown = (text, applyHighlights = null) => {
    if (!text) return null;
    const lines = text.split('\n');
    const elements = [];
    let currentList = null;
    let currentListType = null;
    let listKey = 0;

    const flushList = () => {
      if (currentList && currentList.length > 0) {
        if (currentListType === 'bullet') {
          elements.push(
            <ul key={`list-${listKey++}`} className="md-list">
              {currentList.map((item, i) => (
                <li key={i}>{renderInlineMarkdown(item, applyHighlights)}</li>
              ))}
            </ul>
          );
        } else {
          elements.push(
            <ol key={`list-${listKey++}`} className="md-list">
              {currentList.map((item, i) => (
                <li key={i}>{renderInlineMarkdown(item, applyHighlights)}</li>
              ))}
            </ol>
          );
        }
        currentList = null;
        currentListType = null;
      }
    };

    lines.forEach((line, lineIdx) => {
      const trimmed = line.trim();
      if (!trimmed) { flushList(); return; }

      const headerMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
      if (headerMatch) {
        flushList();
        const level = headerMatch[1].length;
        elements.push(
          <div key={lineIdx} className={`md-header md-h${level}`}>
            {renderInlineMarkdown(headerMatch[2], applyHighlights)}
          </div>
        );
        return;
      }

      const bulletMatch = trimmed.match(/^[-•*]\s+(.+)$/);
      if (bulletMatch) {
        if (currentListType !== 'bullet') { flushList(); currentList = []; currentListType = 'bullet'; }
        currentList.push(bulletMatch[1]);
        return;
      }

      const numberedMatch = trimmed.match(/^\d+[.)]\s+(.+)$/);
      if (numberedMatch) {
        if (currentListType !== 'numbered') { flushList(); currentList = []; currentListType = 'numbered'; }
        currentList.push(numberedMatch[1]);
        return;
      }

      flushList();
      elements.push(
        <p key={lineIdx} className="md-paragraph">
          {renderInlineMarkdown(trimmed, applyHighlights)}
        </p>
      );
    });

    flushList();
    return <div className="md-content">{elements}</div>;
  };

  const renderInlineMarkdown = (text, applyHighlights) => {
    if (!text) return null;
    const parts = [];
    let remaining = text;
    let key = 0;

    while (remaining) {
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
      if (boldMatch) {
        if (boldMatch.index > 0) {
          const beforeText = remaining.slice(0, boldMatch.index);
          parts.push(<span key={key++}>{applyHighlights ? applyHighlights(beforeText) : beforeText}</span>);
        }
        parts.push(<strong key={key++}>{applyHighlights ? applyHighlights(boldMatch[1]) : boldMatch[1]}</strong>);
        remaining = remaining.slice(boldMatch.index + boldMatch[0].length);
      } else {
        parts.push(<span key={key++}>{applyHighlights ? applyHighlights(remaining) : remaining}</span>);
        remaining = '';
      }
    }
    return parts.length > 0 ? parts : text;
  };

  // Scroll handling - compact when not at bottom, unless input is focused
  const checkScrollPosition = useCallback(() => {
    const container = feedContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    // Show scroll down button when far from bottom
    setShowScrollDown(distanceFromBottom > 100);

    // Track if at bottom (within 50px)
    const atBottom = distanceFromBottom < 50;
    setIsAtBottom(atBottom);

    // Compact when not at bottom (but focus state takes priority)
    if (!isInputFocused) {
      setIsCompact(!atBottom);
    }
  }, [isInputFocused]);

  const scrollToBottom = useCallback((smooth = true) => {
    if (feedEndRef.current) {
      feedEndRef.current.scrollIntoView({ behavior: smooth ? 'smooth' : 'instant' });
    }
  }, []);

  // Scroll to bottom when new entries are added
  useEffect(() => {
    if (entries.length === 0) return;
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      prevEntriesLength.current = entries.length;
      setTimeout(() => scrollToBottom(false), 0);
    } else if (entries.length > prevEntriesLength.current) {
      prevEntriesLength.current = entries.length;
      scrollToBottom(true);
    }
  }, [entries, scrollToBottom]);

  // Scroll when the most recent entry updates (AI response comes in)
  const latestEntry = entries[0];
  const lastScrolledEntryId = useRef(null);

  useEffect(() => {
    // Only act on new received answers we haven't scrolled to yet
    if (latestEntry?.type === 'answer' &&
        latestEntry?.status === 'received' &&
        latestEntry?.message &&
        latestEntry?.id !== lastScrolledEntryId.current) {

      lastScrolledEntryId.current = latestEntry.id;

      // Scroll to bottom
      setTimeout(() => scrollToBottom(true), 200);
    }
  }, [latestEntry, scrollToBottom]);

  // Scroll during streaming
  useEffect(() => {
    if (streamingText) scrollToBottom(false);
  }, [streamingText, scrollToBottom]);

  // Scroll to specific entry when requested
  useEffect(() => {
    if (scrollToEntryId && entryRefs.current[scrollToEntryId]) {
      entryRefs.current[scrollToEntryId].scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedEntryId(scrollToEntryId);
      // Clear highlight after animation
      const timer = setTimeout(() => {
        setHighlightedEntryId(null);
        onScrollComplete?.();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [scrollToEntryId, onScrollComplete]);

  useEffect(() => {
    const container = feedContainerRef.current;
    if (!container) return;
    container.addEventListener('scroll', checkScrollPosition);
    return () => container.removeEventListener('scroll', checkScrollPosition);
  }, [checkScrollPosition]);

  // When input is focused, expand the bar
  useEffect(() => {
    if (isInputFocused) {
      setIsCompact(false);
    } else if (!isAtBottom) {
      setIsCompact(true);
    }
  }, [isInputFocused, isAtBottom]);

  // Memoize name highlighting pattern and map - expensive regex computation
  const { namePattern, nameMap } = useMemo(() => {
    const donorNames = Object.keys(donors || {});
    const patterns = [];
    const map = {};
    donorNames.forEach(fullName => {
      const escapedFull = fullName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      patterns.push(escapedFull);
      map[fullName.toLowerCase()] = fullName;
      const firstName = fullName.split(' ')[0];
      if (firstName.length >= 3) {
        patterns.push(firstName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
        map[firstName.toLowerCase()] = fullName;
      }
    });
    patterns.sort((a, b) => b.length - a.length);

    const amountPattern = '\\$[\\d,]+[kKmM]?|\\b\\d{1,3}(?:,\\d{3})+(?:[kKmM])?\\b|\\b\\d+[kKmM]\\b';
    const allPatternsArray = [amountPattern];
    if (patterns.length > 0) {
      allPatternsArray.push('\\b(?:' + patterns.join('|') + ')\\b');
    }
    const combinedPattern = '(' + allPatternsArray.join('|') + ')';
    return { namePattern: new RegExp(combinedPattern, 'gi'), nameMap: map };
  }, [donors]);

  // Sanitize messages to strip raw JSON that may leak through on connection errors
  const sanitizeMessage = (msg) => {
    if (!msg || typeof msg !== 'string') return msg;
    // Remove JSON objects that look like AI action responses
    return msg
      .replace(/\{"type":\s*"[^"]*",\s*"data":\s*\{[^}]*\}\}/g, '')
      .replace(/\{"type":\s*"[^"]*",\s*"data":\s*\{[^}]*"[^}]*\}\}/g, '')
      .replace(/\{[^{}]*"type":\s*"(update_person|add_person|open_add_modal|show_person|clarify_person)"[^{}]*\}/g, '')
      .trim() || 'Done.';
  };

  // Highlight names and amounts
  const renderMessageWithNames = useCallback((message) => {
    if (!message) return message;

    // Sanitize first to remove any raw JSON
    const cleanMessage = sanitizeMessage(message);
    if (!cleanMessage) return cleanMessage;

    const parts = cleanMessage.split(namePattern);
    const isAmount = (str) => /^(\$[\d,]+[kKmM]?|\d{1,3}(?:,\d{3})+[kKmM]?|\d+[kKmM])$/i.test(str);

    return parts.map((part, i) => {
      if (!part) return null;
      if (isAmount(part)) {
        return <span key={i} className="feed-amount-highlight">{part}</span>;
      }
      const matchedName = nameMap[part.toLowerCase()];
      if (matchedName) {
        return (
          <span key={i} className="feed-name-highlight" onClick={(e) => { e.stopPropagation(); onNameTap?.(matchedName); }}>
            {part}
          </span>
        );
      }
      return part;
    });
  }, [namePattern, nameMap, onNameTap]);

  // Day labels
  const getDayLabel = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  };

  const getCapturedIcon = (type) => {
    switch (type) {
      case 'meeting': case 'amount': case 'logged': return '✓';
      case 'task': return '→';
      case 'error': return '✕';
      default: return '✓';
    }
  };

  const getCapturedColor = (type) => {
    switch (type) {
      case 'meeting': case 'amount': case 'logged': return '#4ade80';
      case 'task': return '#fbbf24';
      case 'error': return '#ef4444';
      default: return '#4ade80';
    }
  };

  // Render AI response
  const renderRichResponse = (entry) => {
    const { message, people } = entry;
    const isThinking = entry.status === 'thinking';

    if (isThinking && !message) {
      return (
        <div className="feed-thinking">
          <span className="thinking-dot" /><span className="thinking-dot" /><span className="thinking-dot" />
        </div>
      );
    }

    const shouldAnimate = entry.id === animatingEntryId;

    return (
      <>
        {message && (
          <div className="feed-entry-message">
            {shouldAnimate ? (
              <WordReveal animate={true} speed={40}>
                {renderMarkdown(message, renderMessageWithNames)}
              </WordReveal>
            ) : (
              renderMarkdown(message, renderMessageWithNames)
            )}
          </div>
        )}

        {/* Group selection for newly added donors */}
        {entry.groupSelection && (
          <div className="desktop-group-selection">
            <div className="group-selection-label">Add to a group?</div>
            <div className="group-selection-options">
              {entry.groupSelection.groups.map((group) => (
                <button
                  key={group.id}
                  className="group-option"
                  onClick={(e) => {
                    e.stopPropagation();
                    onGroupSelect?.(
                      entry.id,
                      entry.groupSelection.donorId,
                      entry.groupSelection.donorName,
                      group.id,
                      group.name
                    );
                  }}
                >
                  <span className="group-option-dot" style={{ background: group.color || '#a78bfa' }} />
                  <span className="group-option-name">{group.name}</span>
                </button>
              ))}
              <button
                className="group-option group-option-new"
                onClick={(e) => {
                  e.stopPropagation();
                  onGroupSelect?.(
                    entry.id,
                    entry.groupSelection.donorId,
                    entry.groupSelection.donorName,
                    'new',
                    null
                  );
                }}
              >
                <span className="group-option-icon">+</span>
                <span className="group-option-name">New Group</span>
              </button>
              <button
                className="group-option group-option-skip"
                onClick={(e) => {
                  e.stopPropagation();
                  onSkipGroupSelect?.(entry.id, entry.groupSelection.donorName, entry.groupSelection.donorId);
                }}
              >
                <span className="group-option-name">Skip</span>
              </button>
            </div>
          </div>
        )}

        {/* Prompt to create first group (onboarding) */}
        {entry.promptCreateGroup && (
          <CreateGroupPrompt
            forPerson={entry.promptCreateGroup.forPerson}
            suggestedName={entry.promptCreateGroup.suggestedName}
            onCreateGroup={(groupName) => onCreateGroup?.(entry.id, groupName, entry.promptCreateGroup.forPerson)}
          />
        )}

        {/* Auto-researching state */}
        {entry.researching && (
          <div className="desktop-researching-state">
            <div className="researching-spinner"></div>
            <span>Researching {entry.researching.name}...</span>
          </div>
        )}

        {/* Research prompt after adding (legacy - no longer used) */}
        {entry.askResearch && (
          <div className="desktop-research-prompt">
            <div className="research-prompt-text">
              Would you like me to research {entry.askResearch.name.split(' ')[0]}?
            </div>
            <div className="research-prompt-buttons">
              <button
                className="research-btn confirm"
                onClick={(e) => {
                  e.stopPropagation();
                  onResearchPerson?.(entry.id, entry.askResearch.name);
                }}
              >
                Research
              </button>
              <button
                className="research-btn skip"
                onClick={(e) => {
                  e.stopPropagation();
                  onSkipResearch?.(entry.id, entry.askResearch.name, entry.askResearch.donorId);
                }}
              >
                Skip
              </button>
            </div>
          </div>
        )}

        {/* Ask for more info prompt */}
        {entry.askMoreInfo && (
          <div className="desktop-more-info-prompt">
            <div className="more-info-skip">
              <button
                className="research-btn skip"
                onClick={(e) => {
                  e.stopPropagation();
                  onSkipMoreInfo?.(entry.id, entry.askMoreInfo.name);
                }}
              >
                Skip
              </button>
            </div>
          </div>
        )}

        {/* Group selection for list creation */}
        {entry.listGroupSelection && (
          <div className="desktop-list-group-selection">
            <div className="list-group-prompt">Which group would you like me to organize?</div>
            <div className="list-group-buttons">
              {entry.listGroupSelection.groups.map((group) => (
                <button
                  key={group.id}
                  className="list-group-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateListForGroup?.(
                      entry.id,
                      group,
                      entry.listGroupSelection.listPrompt,
                      entry.listGroupSelection.suggestedName
                    );
                  }}
                >
                  <span className="list-group-dot" style={{ background: group.color || '#a78bfa' }} />
                  <span className="list-group-name">{group.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {people && people.length > 0 && (
          <div className="response-people">
            {people.map((person, i) => (
              <div key={i} className="response-person-card" onClick={(e) => { e.stopPropagation(); onNameTap?.(person.name); }}>
                <div className="person-card-avatar" style={{ background: getAvatarColor(person.name) }}>
                  {getInitials(person.name)}
                </div>
                <div className="person-card-info">
                  <div className="person-card-name">{person.name}</div>
                  {person.reason && <div className="person-card-reason">{person.reason}</div>}
                </div>
                <span className="person-card-arrow">›</span>
              </div>
            ))}
          </div>
        )}

        {entry.captured && entry.captured.length > 0 && (
          <div className="feed-entry-captured">
            {entry.captured.map((item, i) => (
              <span key={i} className="captured-item" style={{ color: getCapturedColor(item.type) }}>
                {getCapturedIcon(item.type)} {item.text}
              </span>
            ))}
          </div>
        )}

        {entry.listPreview && (
          <div className="list-preview" onClick={(e) => { e.stopPropagation(); onViewList?.(entry.listPreview); }}>
            <div className="list-preview-groups">
              {entry.listPreview.groups?.map((group, i) => (
                <div key={i} className="list-preview-group">
                  <div className="list-preview-dot" style={{ background: group.color || '#a78bfa' }} />
                  <span className="list-preview-name">{group.name}</span>
                  <span className="list-preview-count">{group.people?.length || 0}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  // Reverse and group entries - memoized to prevent lag on typing
  const { entriesByDay, sortedDays } = useMemo(() => {
    const reversed = [...entries].reverse();
    const byDay = reversed.reduce((acc, entry) => {
      const day = getDayLabel(entry.timestamp);
      if (!acc[day]) acc[day] = [];
      acc[day].push(entry);
      return acc;
    }, {});
    const days = Object.keys(byDay).sort((a, b) => {
      const dateA = new Date(byDay[a][0]?.timestamp || 0);
      const dateB = new Date(byDay[b][0]?.timestamp || 0);
      return dateA - dateB;
    });
    return { entriesByDay: byDay, sortedDays: days };
  }, [entries]);

  return (
    <div className="desktop-feed">
      {/* Scroll down button */}
      {showScrollDown && (
        <button className="scroll-down-btn" onClick={() => scrollToBottom(true)}>↓</button>
      )}

      {/* Messages area */}
      <div className="feed-messages" ref={feedContainerRef}>
        {loading ? (
          <div className="feed-loading">Loading...</div>
        ) : entries.length === 0 ? (
          <div className="feed-spacer" />
        ) : (
          <>
            <div className="feed-spacer" />
            {sortedDays.map((day) => (
              <div key={day} className="feed-day-group">
                <div className="feed-day-label">{day}</div>
                {entriesByDay[day].map((entry) => {
                  const isHighlighted = highlightedEntryId === entry.id;
                  if (entry.type === 'question') {
                    return (
                      <div
                        key={entry.id}
                        ref={el => entryRefs.current[entry.id] = el}
                        className={`feed-entry feed-question ${isHighlighted ? 'highlighted' : ''}`}
                      >
                        <div className="feed-entry-message">{renderMessageWithNames(entry.message)}</div>
                      </div>
                    );
                  }
                  if (entry.type === 'answer') {
                    return (
                      <div
                        key={entry.id}
                        ref={el => entryRefs.current[entry.id] = el}
                        className={`feed-entry feed-answer ${entry.status === 'streaming' ? 'streaming' : ''} ${isHighlighted ? 'highlighted' : ''}`}
                      >
                        {renderRichResponse(entry)}
                      </div>
                    );
                  }
                  return (
                    <div
                      key={entry.id}
                      ref={el => entryRefs.current[entry.id] = el}
                      className={`feed-entry feed-note ${entry.status === 'sending' ? 'sending' : ''} ${isHighlighted ? 'highlighted' : ''}`}
                    >
                      <div className="feed-entry-message">{renderMessageWithNames(entry.message)}</div>
                      {entry.captured && entry.captured.length > 0 && (
                        <div className="feed-entry-captured">
                          {entry.captured.map((item, i) => (
                            <span key={i} className="captured-item" style={{ color: getCapturedColor(item.type) }}>
                              {getCapturedIcon(item.type)} {item.text}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
            <div ref={feedEndRef} />
          </>
        )}
      </div>

      {/* Input bar - separate component to prevent re-renders */}
      <FeedInput
        onSubmit={onSubmit}
        isSubmitting={isSubmitting}
        placeholder={placeholder}
        isCompact={isCompact}
        onFocusChange={setIsInputFocused}
        onAddPerson={onAddPerson}
        isOnboarding={isOnboarding}
      />
    </div>
  );
}

export default DesktopFeed;
